import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Trash2, 
  ShoppingBag, 
  ArrowRight, 
  Sparkles, 
  Layers, 
  MessageCircle, 
  ShieldCheck, 
  Truck,
  RotateCcw
} from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { resolveImageUrl } from "@/utils/api";

export default function Cart() {
  const { cartItems, updateQuantity, removeFromCart, clearCart, subtotal, totalItemsCount } = useCart();
  const { settings } = useSiteSettings();
  const navigate = useNavigate();

  const freeDeliveryThreshold = settings.free_delivery_threshold || 5000;
  const standardDelivery = settings.standard_delivery_charge || 250;
  const isFreeDelivery = subtotal >= freeDeliveryThreshold;
  const shippingFee = isFreeDelivery ? 0 : standardDelivery;
  const grandTotal = subtotal + (cartItems.length > 0 ? shippingFee : 0);
  const progressToFreeShipping = Math.min(100, (subtotal / freeDeliveryThreshold) * 100);

  const handleWhatsAppEntireCart = () => {
    if (cartItems.length === 0) return;
    const phone = settings.whatsapp_number.replace(/[^0-9]/g, "");
    const itemsList = cartItems.map((item, idx) => 
      `${idx + 1}. *${item.product_name}*%0A   Color: ${item.selected_color}%0A   Design: ${item.selected_design}%0A   Qty: ${item.quantity} Complete Sets%0A   Subtotal: Rs. ${item.item_total.toLocaleString()}`
    ).join("%0A%0A");

    const message = encodeURIComponent(
      `Assalam o Alaikum! I want to order the following items from my cart at Anwer Fancy Lace:%0A%0A`
    ) + itemsList + encodeURIComponent(
      `%0A%0A*Total Cart Value:* Rs. ${subtotal.toLocaleString()} PKR%0APlease process my order.`
    );
    window.open(`https://wa.me/${phone}?text=${message}`, "_blank");
  };

  if (cartItems.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center space-y-6">
        <div className="w-20 h-20 rounded-full bg-[#FAF8F5] border-2 border-[#D4AF37]/40 flex items-center justify-center mx-auto text-[#996515]">
          <ShoppingBag className="w-10 h-10" />
        </div>
        <h2 data-testid="empty-cart-message" className="font-heading text-2xl sm:text-3xl font-bold text-stone-900">
          Your Shopping Cart is Empty
        </h2>
        <p className="text-xs sm:text-sm text-stone-500 max-w-md mx-auto">
          Explore our collection of China-imported diamond sitara laces, zari borders, and premium complete lace sets.
        </p>
        <Link
          to="/shop"
          data-testid="cart-empty-browse-btn"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] text-xs font-bold rounded-xl shadow-md transition"
        >
          <span>Browse Complete Sets Catalog</span>
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      
      {/* Title */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#E5E0D8] pb-4">
        <div>
          <h1 data-testid="cart-page-title" className="font-heading text-2xl sm:text-3xl font-bold text-stone-900">
            Shopping Cart ({totalItemsCount} Complete Sets)
          </h1>
          <p className="text-xs text-stone-500 mt-1">
            Review your complete lace set packs before proceeding to checkout.
          </p>
        </div>
        <button
          type="button"
          onClick={clearCart}
          data-testid="cart-clear-btn"
          className="text-xs text-stone-500 hover:text-rose-600 flex items-center gap-1 font-medium transition"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          <span>Clear Cart</span>
        </button>
      </div>

      {/* Free Shipping Tracker */}
      <div className="bg-[#FAF8F5] p-4 rounded-xl border border-[#D4AF37]/40 space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="font-semibold text-stone-800 flex items-center gap-1.5">
            <Truck className="w-4 h-4 text-[#D4AF37]" />
            {isFreeDelivery ? (
              <span className="text-emerald-800 font-bold">🎉 Congratulations! You have unlocked FREE Nationwide Delivery!</span>
            ) : (
              <span>Add <strong className="text-[#996515]">Rs. {(freeDeliveryThreshold - subtotal).toLocaleString()}</strong> more to get <strong>FREE Delivery</strong></span>
            )}
          </span>
          <span className="font-bold text-stone-700">{progressToFreeShipping.toFixed(0)}%</span>
        </div>
        <div className="w-full h-2 bg-stone-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gold-gradient transition-all duration-500 rounded-full"
            style={{ width: `${progressToFreeShipping}%` }}
          />
        </div>
      </div>

      {/* Cart Layout: Items (Left 8 cols) & Summary (Right 4 cols) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Items List */}
        <div className="lg:col-span-8 space-y-4">
          {cartItems.map((item) => (
            <div
              key={item.cartItemId}
              data-testid={`cart-item-${item.cartItemId}`}
              className="bg-white p-4 sm:p-5 rounded-2xl border border-[#E5E0D8] shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            >
              {/* Image & Title */}
              <div className="flex items-center gap-4">
                <img
                  src={resolveImageUrl(item.image)}
                  alt={item.product_name}
                  className="w-20 h-20 rounded-xl object-cover border border-stone-200 bg-[#FAF8F5] shrink-0"
                />
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-[#996515] bg-[#FAF8F5] px-2 py-0.5 rounded border border-[#E5E0D8]">
                      {item.category || "Complete Set"}
                    </span>
                    {item.is_complete_set && (
                      <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 px-1.5 py-0.5 rounded">
                        Complete Set Pack
                      </span>
                    )}
                  </div>
                  <h3 className="font-heading font-semibold text-sm text-stone-900 leading-snug">
                    {item.product_name}
                  </h3>
                  <div className="text-xs text-stone-500 flex items-center gap-3">
                    <span>Color: <strong className="text-stone-700">{item.selected_color}</strong></span>
                    <span>Design: <strong className="text-stone-700">{item.selected_design}</strong></span>
                  </div>
                  <div className="text-xs font-bold text-[#996515]">
                    Rs. {item.price.toLocaleString()} per complete set
                  </div>
                </div>
              </div>

              {/* Quantity Controls & Item Total */}
              <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto gap-6 border-t sm:border-t-0 pt-3 sm:pt-0 border-stone-100">
                
                {/* Stepper */}
                <div className="flex items-center border border-stone-300 rounded-lg bg-[#FAF8F5]">
                  <button
                    type="button"
                    onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                    data-testid={`btn-decrease-qty-${item.cartItemId}`}
                    className="px-2.5 py-1 text-stone-600 hover:bg-stone-200 text-xs font-bold"
                  >
                    -
                  </button>
                  <span className="px-3 py-1 text-xs font-bold text-stone-900 min-w-[28px] text-center">
                    {item.quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                    data-testid={`btn-increase-qty-${item.cartItemId}`}
                    className="px-2.5 py-1 text-stone-600 hover:bg-stone-200 text-xs font-bold"
                  >
                    +
                  </button>
                </div>

                {/* Subtotal */}
                <div className="text-right min-w-[100px]">
                  <span className="text-xs text-stone-400 block sm:hidden">Total</span>
                  <span 
                    data-testid={`cart-item-total-${item.cartItemId}`}
                    className="font-bold text-sm text-stone-900"
                  >
                    Rs. {item.item_total.toLocaleString()}
                  </span>
                </div>

                {/* Delete */}
                <button
                  type="button"
                  onClick={() => removeFromCart(item.cartItemId)}
                  data-testid={`btn-remove-item-${item.cartItemId}`}
                  className="p-1.5 text-stone-400 hover:text-rose-600 transition"
                  aria-label="Remove item"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

            </div>
          ))}
        </div>

        {/* Order Summary Card */}
        <div className="lg:col-span-4 space-y-4">
          <div className="bg-white p-6 rounded-2xl border border-[#E5E0D8] shadow-luxury space-y-5">
            <h3 className="font-heading font-bold text-base text-stone-900 border-b border-[#E5E0D8] pb-3">
              Order Summary
            </h3>

            <div className="space-y-3 text-xs">
              <div className="flex justify-between text-stone-600">
                <span>Subtotal ({totalItemsCount} Sets)</span>
                <span data-testid="cart-subtotal" className="font-bold text-stone-900">
                  Rs. {subtotal.toLocaleString()}
                </span>
              </div>

              <div className="flex justify-between text-stone-600">
                <span>Estimated Nationwide Delivery</span>
                <span>
                  {isFreeDelivery ? (
                    <span className="text-emerald-700 font-bold">FREE</span>
                  ) : (
                    <span className="font-semibold text-stone-800">Rs. {standardDelivery}</span>
                  )}
                </span>
              </div>

              <div className="pt-3 border-t border-[#E5E0D8] flex justify-between items-baseline">
                <div>
                  <span className="font-heading font-bold text-base text-stone-900 block">Total Amount</span>
                  <span className="text-[10px] text-stone-400">Cash on Delivery / Bank Transfer</span>
                </div>
                <div className="text-right">
                  <span 
                    data-testid="cart-grand-total"
                    className="font-heading text-2xl font-extrabold text-stone-900"
                  >
                    Rs. {grandTotal.toLocaleString()}
                  </span>
                  <span className="text-[11px] text-stone-500 font-normal block">PKR</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2.5 pt-2">
              <button
                type="button"
                onClick={() => navigate("/checkout")}
                data-testid="cart-proceed-checkout-btn"
                className="w-full py-3.5 px-4 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] text-xs font-bold rounded-xl shadow-md transition flex items-center justify-center gap-2"
              >
                <span>Proceed to Checkout</span>
                <ArrowRight className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={handleWhatsAppEntireCart}
                data-testid="cart-whatsapp-order-btn"
                className="w-full py-3 px-4 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold rounded-xl shadow-sm transition flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                <span>Order Entire Cart on WhatsApp</span>
              </button>
            </div>

            <div className="pt-3 border-t border-[#E5E0D8] text-[11px] text-stone-500 space-y-1.5">
              <p className="flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span>Safe Delivery with Cash on Delivery Option</span>
              </p>
              <p className="flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span>Original China Import Quality Assurance</span>
              </p>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
}
